import { parsePersonName } from "@/lib/tool-kit/citation-parts";

/**
 * Names and sentences shared by the journal-statement generators.
 *
 * Contribution, competing-interest and funding statements refer to authors either by
 * full name ("Jane Smith: Conceptualization") or by initials ("J.S. received consulting
 * fees"), and journals differ on which — so every generator offers both, and they all
 * initialise a name the same way.
 */

export type NameStyle = "full" | "initials";

const RE_HYPHEN = /-/;
const RE_WHITESPACE = /\s+/;
const RE_FIRST_LETTER = /\p{L}/u;
const RE_LOWER_START = /^\p{Ll}/u;
/**
 * A sentence already ends when its last mark is a full stop, question mark, exclamation
 * mark or colon in any script the site is written in: Latin, CJK fullwidth, Arabic and
 * Devanagari. Without the non-Latin marks a Chinese declaration came out as "…。.".
 */
const RE_TERMINAL = /[.?!:。！？：؟।]["”’)」』）]*$/;
/** Chinese and Japanese close a sentence with the ideographic full stop. */
const RE_HAN_OR_KANA_END =
  /[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}]["”’)」』）]*$/u;
const RE_FINAL_PERIODS = /\.+$/;
const RE_FINAL_WORD = /(\p{L}+)\.+$/u;
/** A doubled full stop that is not an ellipsis: "Inc.." → "Inc." */
const RE_DOUBLED_PERIOD = /(?<!\.)\.\.(?!\.)/g;

/**
 * Abbreviations whose full stop is part of the word, so it must survive when a field
 * typed with one is dropped into the middle of a sentence ("Acme Inc. (paid to …)").
 */
const ABBREVIATIONS = new Set([
  "al",
  "approx",
  "co",
  "corp",
  "dept",
  "dr",
  "etc",
  "inc",
  "jr",
  "llc",
  "ltd",
  "no",
  "plc",
  "prof",
  "sr",
  "st",
  "univ",
  "vs",
]);

/** "Jean-Michel" → "J.-M."; a particle ("van") keeps its lowercase initial. */
function initialOf(token: string, keepCase: boolean): string {
  return token
    .split(RE_HYPHEN)
    .filter(Boolean)
    .map((part) => {
      const letter = RE_FIRST_LETTER.exec(part)?.[0] ?? part.charAt(0);
      return `${keepCase ? letter : letter.toUpperCase()}.`;
    })
    .join("-");
}

/**
 * Initials as contribution statements write them: "Mary Jane Smith" → "M.J.S.",
 * "Smith, Mary Jane" → "M.J.S.", "Jean-Michel Basquiat" → "J.-M.B.",
 * "Vincent van Gogh" → "V.v.G.". Suffixes (Jr., III) are dropped.
 */
export function authorInitials(name: string): string {
  const parsed = parsePersonName(name);
  if (!parsed) {
    return "";
  }
  const given = parsed.given.map((token) => initialOf(token, false));
  const family = parsed.family
    .split(RE_WHITESPACE)
    .filter(Boolean)
    .map((token) => initialOf(token, RE_LOWER_START.test(token)));
  return [...given, ...family].join("");
}

/** The name as the statement should print it. */
export function authorLabel(name: string, style: NameStyle): string {
  const trimmed = name.trim().replace(RE_WHITESPACE, " ");
  return style === "initials" ? authorInitials(trimmed) : trimmed;
}

/** Initials that two or more of the listed authors would share. */
export function sharedInitials(names: readonly string[]): string[] {
  const seen = new Map<string, number>();
  for (const name of names) {
    const initials = authorInitials(name);
    if (initials) {
      seen.set(initials, (seen.get(initials) ?? 0) + 1);
    }
  }
  return [...seen.entries()]
    .filter(([, count]) => count > 1)
    .map(([initials]) => initials);
}

/**
 * Capitalise the first letter, so a name typed "the X Committee" can open a sentence.
 * Text that does not start with a letter — a "[committee name]" placeholder — is left
 * alone, so the placeholder still reads exactly as the widget looks for it.
 */
export function capitalizeFirst(text: string): string {
  if (text.search(RE_FIRST_LETTER) !== 0) {
    return text;
  }
  return `${text.charAt(0).toUpperCase()}${text.slice(1)}`;
}

/**
 * Trim, collapse a doubled full stop, and add a full stop unless the text already ends a
 * sentence. The doubled stop is what an abbreviation at the end of a field produces when
 * a template closes the sentence after it: "… Acme Inc." + "." → "… Acme Inc."
 */
export function asSentence(text: string): string {
  const trimmed = text.trim().replace(RE_DOUBLED_PERIOD, ".");
  if (trimmed === "" || RE_TERMINAL.test(trimmed)) {
    return trimmed;
  }
  return `${trimmed}${RE_HAN_OR_KANA_END.test(trimmed) ? "。" : "."}`;
}

/**
 * Drop the full stop a user typed at the end of a clause the template will close —
 * "because they are sensitive." — but keep the one that belongs to the last word:
 * initials ("P.R."), "Inc.", "et al.".
 */
export function stripFinalPeriod(text: string): string {
  const trimmed = text.trim();
  const word = RE_FINAL_WORD.exec(trimmed)?.[1];
  if (!word || word.length < 3 || ABBREVIATIONS.has(word.toLowerCase())) {
    return trimmed;
  }
  return trimmed.replace(RE_FINAL_PERIODS, "");
}

/** "A, B, and C" in the page's language. */
export function listConjunction(
  items: readonly string[],
  locale: string
): string {
  return new Intl.ListFormat(locale, {
    style: "long",
    type: "conjunction",
  }).format(items);
}
